import 'twin.macro'

import { useAppSelector } from 'hooks'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useObtainCreditBankManagerQuery } from 'redux/api/creditApi'
import { getCurrentFlowType, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw, { TwStyle } from 'twin.macro'
import { getWsStepStatus, getWsStepStatuses } from 'utils'
import Emitter from 'utils/emitter'
import { v4 as uuidv4 } from 'uuid'

import BankManagerCode from '../BankManagerCode'
import FlowStepsList, { FlowStepType } from '../FlowSteps'

type DocumentsGeneratingProps = {
  twStyle?: TwStyle
}

enum StepBlocksName {
  ESTIMATION_DECISION = 'ESTIMATION_DECISION',
  COLVIR = 'COLVIR',
  DOCUMENTS_GENERATING = 'DOCUMENTS_GENERATING',
  CRED_ADMIN = 'CRED_ADMIN'
}

const DocumentsGenerating = ({ twStyle }: DocumentsGeneratingProps) => {
  // Tools
  const { t } = useTranslation()

  // Selectors
  const {
    REGISTER_IN_COLVIR,
    COLVIR_MORTGAGE,
    GENERATE_DOCUMENTS_SIGNED_BY_ECP,
    GENERATE_MAIN_DOCUMENTS,
    CREATE_APPLICATION_IN_CRED_ADMIN,
    GET_CORP_CARD_DATA,
    DECISION_ESTIMATOR,
    WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN,
    GENERATE_CONCLUSION_ON_COLLATERAL,
    SEND_LOAN_DATA_TO_CRED_ADMIN,
    CHECK_DAILY_LIMIT
  } = useAppSelector(getNotificationsSelector)

  const { current_step } = useAppSelector(getStatusSelector)
  const currentFlowType = useAppSelector(getCurrentFlowType)
  const { id: creditID } = useAppSelector(getActiveCreditApplication)

  // State
  const [isBankManagerAttachedNow, setIsBankManagerAttachedNow] = useState(false)

  // Queries
  const { data, isLoading, isSuccess } = useObtainCreditBankManagerQuery(creditID as string, {
    skip: !creditID
  })

  // Handlers
  const handleBankManagerAttached = () => {
    setIsBankManagerAttachedNow(true)
    Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
  }

  // Computed | memoized values
  const estimatorDecisionStepStatus = useMemo<FlowStepType>(() => {
    return {
      stepsName: StepBlocksName.ESTIMATION_DECISION,
      status: getWsStepStatuses(DECISION_ESTIMATOR, GENERATE_CONCLUSION_ON_COLLATERAL),
      apiSteps: [FLOW_STEPS.DECISION_ESTIMATOR, FLOW_STEPS.GENERATE_CONCLUSION_ON_COLLATERAL],
      id: uuidv4(),
      title:
        getWsStepStatus(DECISION_ESTIMATOR) === 'DONE'
          ? t('flow-decision-estimator-done')
          : t('flow-decision-estimator'),
      subtitle: getWsStepStatus(DECISION_ESTIMATOR) === 'DONE' ? undefined : t('flow-sms-after')
    }
  }, [DECISION_ESTIMATOR, GENERATE_CONCLUSION_ON_COLLATERAL, t])

  const colvirStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        id: uuidv4(),
        stepsName: StepBlocksName.COLVIR,
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        title: t('flow-deal-register'),
        subtitle: t(t('flow-deal-register2'))
      }
    if (currentFlowType === 'COLLATERAL' || currentFlowType === 'THIRD_PARTY_PLEDGE') {
      return {
        id: uuidv4(),
        stepsName: StepBlocksName.COLVIR,
        status: getWsStepStatuses(REGISTER_IN_COLVIR, COLVIR_MORTGAGE),
        apiSteps: [FLOW_STEPS.REGISTER_IN_COLVIR, FLOW_STEPS.COLVIR_MORTGAGE],
        title: t('flow-deal-register'),
        subtitle: t(t('flow-deal-register2'))
      }
    }
    if (currentFlowType === 'OWN_AUTO') {
      return {
        id: uuidv4(),
        stepsName: StepBlocksName.COLVIR,
        status: getWsStepStatuses(GET_CORP_CARD_DATA, REGISTER_IN_COLVIR, SEND_LOAN_DATA_TO_CRED_ADMIN),
        apiSteps: [
          FLOW_STEPS.REGISTER_IN_COLVIR,
          FLOW_STEPS.GET_CORP_CARD_DATA,
          FLOW_STEPS.SEND_LOAN_DATA_TO_CRED_ADMIN
        ],
        title: t('flow-deal-register'),
        subtitle: t('flow-deal-register2')
      }
    }
    return {
      id: uuidv4(),
      stepsName: StepBlocksName.COLVIR,
      status: getWsStepStatuses(REGISTER_IN_COLVIR),
      apiSteps: [FLOW_STEPS.REGISTER_IN_COLVIR],
      title: t('flow-deal-register'),
      subtitle: t('flow-deal-register2')
    }
  }, [currentFlowType, t, REGISTER_IN_COLVIR, COLVIR_MORTGAGE, GET_CORP_CARD_DATA, SEND_LOAN_DATA_TO_CRED_ADMIN])

  const documentsStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        id: uuidv4(),
        stepsName: StepBlocksName.DOCUMENTS_GENERATING,
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        title: t('flow-generate-doc-pack'),
        subtitle: t('flow-generate-doc-pack2')
      }

    return {
      id: uuidv4(),
      stepsName: StepBlocksName.DOCUMENTS_GENERATING,
      status: getWsStepStatuses(GENERATE_MAIN_DOCUMENTS, GENERATE_DOCUMENTS_SIGNED_BY_ECP),
      apiSteps: [FLOW_STEPS.GENERATE_MAIN_DOCUMENTS, FLOW_STEPS.GENERATE_DOCUMENTS_SIGNED_BY_ECP],
      title: t('flow-generate-doc-pack'),
      subtitle: t('flow-generate-doc-pack2')
    }
  }, [GENERATE_DOCUMENTS_SIGNED_BY_ECP, GENERATE_MAIN_DOCUMENTS, currentFlowType, t])

  const credAdminStepStatus = useMemo<FlowStepType>(() => {
    if (!currentFlowType)
      return {
        stepsName: StepBlocksName.CRED_ADMIN,
        status: 'STEP_NOT_STARTED',
        apiSteps: [],
        id: uuidv4(),
        title: t('flow-check-dossier'),
        subtitle: t('flow-sms-after')
      }
    if (currentFlowType === 'OWN' || currentFlowType === 'OWN_AUTO' || currentFlowType === 'COLLATERAL') {
      const defaultStatusArray = [CREATE_APPLICATION_IN_CRED_ADMIN, WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN]
      const defaultApiSteps = [
        FLOW_STEPS.CREATE_APPLICATION_IN_CRED_ADMIN,
        FLOW_STEPS.WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN
      ]
      defaultStatusArray.push(CHECK_DAILY_LIMIT)
      defaultApiSteps.push(FLOW_STEPS.CHECK_DAILY_LIMIT)

      return {
        id: uuidv4(),
        stepsName: StepBlocksName.CRED_ADMIN,
        status: getWsStepStatuses(...defaultStatusArray),
        apiSteps: defaultApiSteps,
        title: t('flow-check-dossier'),
        subtitle: t('flow-sms-after')
      }
    }

    return {
      id: uuidv4(),
      stepsName: StepBlocksName.CRED_ADMIN,
      status: getWsStepStatuses(CREATE_APPLICATION_IN_CRED_ADMIN, WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN),
      apiSteps: [FLOW_STEPS.CREATE_APPLICATION_IN_CRED_ADMIN, FLOW_STEPS.WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN],
      title: t('flow-check-dossier'),
      subtitle: t('flow-sms-after')
    }
  }, [CHECK_DAILY_LIMIT, CREATE_APPLICATION_IN_CRED_ADMIN, WAIT_FOR_CONFIRMATION_FROM_CRED_ADMIN, currentFlowType, t])

  console.log(credAdminStepStatus)

  const stepsArray = useMemo(() => {
    if (currentFlowType === 'COLLATERAL' || currentFlowType === 'THIRD_PARTY_PLEDGE') {
      return [estimatorDecisionStepStatus, colvirStepStatus, documentsStepStatus, credAdminStepStatus]
    }
    if (currentFlowType === 'DAMU') {
      return [documentsStepStatus, credAdminStepStatus]
    }
    return [colvirStepStatus, documentsStepStatus, credAdminStepStatus]
  }, [currentFlowType, colvirStepStatus, documentsStepStatus, credAdminStepStatus, estimatorDecisionStepStatus])

  const currStepIdx = useMemo(
    () =>
      stepsArray.findLastIndex(step => step.apiSteps.includes(current_step?.step as FLOW_STEPS)) ||
      stepsArray.findLastIndex(step => step.status === 'IN_PROGRESS' || step.status === 'DONE'),
    [current_step?.step, stepsArray]
  )

  const credAdminStepIdx = useMemo(
    () => stepsArray.findIndex(step => step.stepsName === StepBlocksName.CRED_ADMIN),
    [stepsArray]
  )

  const showBankManagerCondition = useMemo(() => {
    if (isLoading) return true
    if (!isSuccess) return false

    const { bank_manager } = data

    if (!bank_manager) {
      if (credAdminStepStatus.status === 'IN_PROGRESS' || credAdminStepStatus.status === 'DONE') return true
      if (currStepIdx === credAdminStepIdx) return true
    }

    return false
  }, [credAdminStepIdx, credAdminStepStatus.status, currStepIdx, data, isLoading, isSuccess])

  useEffect(() => {
    if (showBankManagerCondition && !isBankManagerAttachedNow) return
    const currentSteps = stepsArray.at(currStepIdx)
    if (!currentSteps) return
    if (currentSteps.status !== 'DONE') return

    Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
  }, [currStepIdx, isBankManagerAttachedNow, showBankManagerCondition, stepsArray])

  return (
    <Fragment>
      <FlowStepsList
        twStyle={twStyle}
        steps={stepsArray}
        isDoneCondition={(_, index) => currStepIdx > index}
        isInProgressCondition={step => step.apiSteps.includes(current_step?.step as FLOW_STEPS)}
      />
      {showBankManagerCondition && (
        <BankManagerCode isLoading={isLoading} twStyle={tw`mt-5`} onAttached={handleBankManagerAttached} />
      )}
    </Fragment>
  )
}

export default DocumentsGenerating
